import React, { useCallback, useState } from 'react';

import { PackagingPicker } from 'components/packaging/components/packagingPOV2/formComponents/PackagingPicker';
import {
    computePackagingItemTotalCost,
    formatPackagingCurrency,
} from 'components/packaging/components/packagingPOV2/utils/packagingPOValidation';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Input } from 'shadcn/components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { Textarea } from 'shadcn/components/ui/Textarea';

const createDraftId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/**
 * @param {Object} props
 * @param {Array} props.items
 * @param {(items: Array) => void} props.onItemsChange
 * @param {boolean} [props.showValidation]
 * @param {string[]} [props.itemErrors]
 */
export function PackagingPOCreateItemsCard({ itemErrors = [], items, onItemsChange, showValidation = false }) {
    const [selectedPackagingOption, setSelectedPackagingOption] = useState(null);

    const handleItemsChange = useCallback(
        (nextItems) => {
            if (typeof onItemsChange === 'function') {
                onItemsChange(Array.isArray(nextItems) ? nextItems : []);
            }
        },
        [onItemsChange],
    );

    const handleRowFieldChange = useCallback(
        (uid, field) => (event) => {
            const value = event?.target?.value ?? '';
            const nextItems = (Array.isArray(items) ? items : []).map((item) =>
                item?.uid === uid
                    ? {
                          ...item,
                          [field]: value,
                      }
                    : item,
            );
            handleItemsChange(nextItems);
        },
        [handleItemsChange, items],
    );

    const handlePackagingChange = useCallback((option) => {
        setSelectedPackagingOption(option ?? null);
    }, []);

    const handleAddItem = useCallback(() => {
        const model = selectedPackagingOption?.model;
        if (!model) return;

        const nextItem = {
            uid: createDraftId(),
            description: model.description ?? '',
            id: Number(model.id),
            masterSku: model.masterSKU ?? '',
            notes: '',
            numPallets: '',
            qty: '',
            unitPrice: '',
            unitsPerCase: String(model.unitsPerCase ?? ''),
            unitsPerCaseUOM: 'ct',
            vendorSku: '',
        };
        handleItemsChange([...(Array.isArray(items) ? items : []), nextItem]);
        setSelectedPackagingOption(null);
    }, [handleItemsChange, items, selectedPackagingOption]);

    const handleRemoveItem = useCallback(
        (itemToRemove) => {
            const targetUid = itemToRemove?.uid;
            const nextItems = (Array.isArray(items) ? items : []).filter((item) => item?.uid !== targetUid);
            handleItemsChange(nextItems);
        },
        [handleItemsChange, items],
    );

    return (
        <Card>
            <CardHeader className="pb-0">
                <CardTitle>Items</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                {showValidation && itemErrors.length > 0 ? (
                    <div className="pb-6">
                        <Alert variant="destructive">
                            <AlertTitle>Fix the following items to save this purchase order</AlertTitle>
                            <AlertDescription>
                                <ul className="list-disc pl-5">
                                    {itemErrors.map((message) => (
                                        <li key={message}>{message}</li>
                                    ))}
                                </ul>
                            </AlertDescription>
                        </Alert>
                    </div>
                ) : null}
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex flex-col gap-2 min-w-[280px]">
                        <PackagingPicker
                            id="packagingSku"
                            enabled
                            label={
                                <>
                                    Packaging SKU <span className="text-destructive">*</span>
                                </>
                            }
                            value={selectedPackagingOption?.value ?? ''}
                            onValueChange={handlePackagingChange}
                            placeholder="Select packaging SKU"
                        />
                    </div>
                    <Button type="button" size="sm" onClick={handleAddItem} disabled={!selectedPackagingOption}>
                        Add Item
                    </Button>
                </div>

                <div className="mt-6">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Item Master SKU</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Quantity (cases)</TableHead>
                                <TableHead>Unit Price</TableHead>
                                <TableHead>Pallets per SKU</TableHead>
                                <TableHead>Units per Case</TableHead>
                                <TableHead>Vendor SKU</TableHead>
                                <TableHead>Notes</TableHead>
                                <TableHead>Total Units</TableHead>
                                <TableHead>Total Cost</TableHead>
                                <TableHead>Remove</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(Array.isArray(items) ? items : []).length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11} className="text-sm text-muted-foreground">
                                        No items added yet.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                (Array.isArray(items) ? items : []).map((item) => {
                                    const totalCost = computePackagingItemTotalCost(item);

                                    return (
                                        <TableRow key={item.uid ?? `${item.id}-${item.vendorSku}-${item.masterSku}`}>
                                            <TableCell>{item.masterSku}</TableCell>
                                            <TableCell>{item.description}</TableCell>
                                            <TableCell>
                                                <Input
                                                    aria-label="Row qty"
                                                    type="number"
                                                    min="1"
                                                    className="w-24"
                                                    value={item.qty ?? ''}
                                                    onChange={handleRowFieldChange(item.uid, 'qty')}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    aria-label="Row unit price"
                                                    type="number"
                                                    inputMode="decimal"
                                                    step="0.001"
                                                    min="0.001"
                                                    className="w-24"
                                                    value={item.unitPrice ?? ''}
                                                    onChange={handleRowFieldChange(item.uid, 'unitPrice')}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    aria-label="Row pallets per sku"
                                                    type="number"
                                                    min="1"
                                                    className="w-24"
                                                    value={item.numPallets ?? ''}
                                                    onChange={handleRowFieldChange(item.uid, 'numPallets')}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    aria-label="Row units per case"
                                                    type="number"
                                                    min="1"
                                                    className="w-24"
                                                    value={item.unitsPerCase ?? ''}
                                                    onChange={handleRowFieldChange(item.uid, 'unitsPerCase')}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    aria-label="Row vendor sku"
                                                    type="text"
                                                    className="w-32"
                                                    value={item.vendorSku ?? ''}
                                                    onChange={handleRowFieldChange(item.uid, 'vendorSku')}
                                                />
                                            </TableCell>
                                            <TableCell className="max-w-[240px]">
                                                <Textarea
                                                    aria-label="Row notes"
                                                    className="min-h-[32px]"
                                                    rows={2}
                                                    value={item.notes ?? ''}
                                                    onChange={handleRowFieldChange(item.uid, 'notes')}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {item.qty && item.unitsPerCase
                                                    ? Number(item.qty) * Number(item.unitsPerCase)
                                                    : ''}
                                            </TableCell>
                                            <TableCell>
                                                {totalCost != null ? formatPackagingCurrency(totalCost) : ''}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleRemoveItem(item)}
                                                >
                                                    Remove
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
}

PackagingPOCreateItemsCard.displayName = 'PackagingPOCreateItemsCard';
